import React from 'react';
import {useTranslation} from 'react-i18next';
import {RequestParam} from '../../../../../../incase-domain/requestParams';
import {PresentationSlugParam} from '../../../../../../service/hooks/useSlug';
import {getRedirectPresetError, isRedirectParam} from '../../../../../../utils/redirect';

interface ParamPresetsProps {
    requestParams: RequestParam[];
    value: PresentationSlugParam[];
    onChange: (params: PresentationSlugParam[]) => void;
}

function ParamPresets({requestParams, value, onChange}: ParamPresetsProps): JSX.Element {
    const {t} = useTranslation();

    const handleChange = (key: string, newValue: string): void => {
        const exists = value.find((p) => p.key === key);
        if (newValue === '') {
            onChange(value.filter((p) => p.key !== key));
        } else if (exists) {
            onChange(value.map((p) => (p.key === key ? {...p, value: newValue} : p)));
        } else {
            onChange([...value, {key, value: newValue}]);
        }
    };

    if (!requestParams?.length) {
        return <p className="c-slugSettings__hint">{t('presEditPage.settings.shortLink.noParams')}</p>;
    }

    return (
        <div className="c-slugSettings__paramList">
            {requestParams.map((rp) => {
                const preset = value.find((p) => p.key === rp.key);
                const isRedirect = isRedirectParam(rp.key);
                const redirectError = isRedirect ? getRedirectPresetError(preset?.value ?? '') : null;
                return (
                    <React.Fragment key={rp.key}>
                        <div className="u-flex u-gap-2 u-v-center">
                            <span className="c-slugSettings__paramKey" title={rp.key}>
                                {rp.key}
                                {rp.required && !isRedirect && <span className="c-slugSettings__paramRequired">*</span>}
                            </span>
                            <div className="c-sidepanel__input c-slugSettings__paramInput">
                                <input
                                    type={isRedirect ? 'url' : 'text'}
                                    placeholder={t(
                                        isRedirect
                                            ? 'presEditPage.settings.shortLink.redirectPlaceholder'
                                            : 'presEditPage.settings.shortLink.paramPresetPlaceholder'
                                    )}
                                    value={preset?.value ?? ''}
                                    onChange={(e) => handleChange(rp.key, isRedirect ? e.target.value.trim() : e.target.value)}
                                />
                            </div>
                        </div>
                        {redirectError && (
                            <span className="is-error c-slugSettings__hint--error">
                                {t(`presEditPage.settings.shortLink.${redirectError}`)}
                            </span>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default ParamPresets;
